import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import icons from 'constants/icons';

import './IconWithText.css';

const IconWithTextSpan = ({
  text,
  textAlign,
  roundedButton,
  squareButton,
  inverseColour,
}) => (
  <span
    className={classNames({
      'icon-with-text__span--inverse': inverseColour && (!roundedButton && !squareButton),
      'icon-with-text__span': !inverseColour || (roundedButton || squareButton),
    })}
    style={{ textAlign }}
  >
    {text}
  </span>
);

IconWithTextSpan.propTypes = {
  text: PropTypes.string,
  textAlign: PropTypes.string,
  roundedButton: PropTypes.bool,
  squareButton: PropTypes.bool,
  inverseColour: PropTypes.bool,
};

IconWithTextSpan.defaultProps = {
  roundedButton: false,
  squareButton: false,
  inverseColour: false,
};

const IconWithTextSpanMultiLine = ({
  text,
  ...props
}) => (
  <React.Fragment>
    {
      text.split('\\n').map((line, i) => (
        <IconWithTextSpan key={line + i.toString()} text={line} {...props} />
      ))
    }
  </React.Fragment>
);

IconWithTextSpanMultiLine.propTypes = {
  text: PropTypes.string,
};

const iconRotation = PropTypes.oneOf([
  0,
  90,
  180,
  270,
]);

const ligatureStyleOverrides = PropTypes.shape({
  color: PropTypes.string,
});

const IconWithTextItalic = ({
  icon,
  rotate,
  preserveIconColour,
  ligatureStyleOverride,
}) => (
  <i
    className={`material-icons icon-with-text__italic${rotate > 0 ? ` icon-with-text__italic--rotate-${rotate}` : ''} ${preserveIconColour ? '' : 'icon-with-text__italic--override-colour'}`}
    style={ligatureStyleOverride}
  >
    {icon}
  </i>
);

IconWithTextItalic.propTypes = {
  icon: PropTypes.string,
  rotate: iconRotation,
  preserveIconColour: PropTypes.bool,
  ligatureStyleOverride: ligatureStyleOverrides,
};

const IconWithText = ({
  textOrientation,
  textAlign,
  text,
  icon,
  multiIcons,
  rotate,
  multiIconsRotate,
  inverseColour,
  preserveIconColour,
  ligatureStyleOverride,
  roundedButton,
  squareButton,
  isFullWidth,
  ...props
}) => (
  // \u00A0 is the unicode escaped form of &nbsp;
  // This prevents React from escaping the ampersand entity-reference as text
  <div>
    <button
      {...props}
      type="button"
      className={classNames({
        'icon-with-text__button--rounded--inverse': roundedButton && inverseColour,
        'icon-with-text__button--rounded': roundedButton && !inverseColour,
        'icon-with-text__button--square--inverse': squareButton && inverseColour,
        'icon-with-text__button--square': squareButton && !inverseColour,
        'icon-with-text__button--inverse': (!squareButton && !roundedButton) && inverseColour,
        'icon-with-text__button': (!squareButton && !roundedButton) && !inverseColour,
        'icon-with-text__button--full-width': isFullWidth,
        'icon-with-text__disabled': props.disabled,
      }, props.className)}
    >
      {
        textOrientation === 'before' ?
          <React.Fragment>
            {
              roundedButton ?
                <IconWithTextSpanMultiLine
                  inverseColour={inverseColour}
                  roundedButton={roundedButton}
                  text={text}
                  textAlign={textAlign}
                />
               :
                <React.Fragment>
                  <IconWithTextSpan inverseColour={inverseColour} roundedButton={roundedButton} squareButton={squareButton} text={text} textAlign={textAlign} />{'\u00A0'}
                </React.Fragment>
            }
          </React.Fragment> : ''
      }
      {
        icon !== '' && multiIcons.length === 0 ?
          <IconWithTextItalic
            rotate={rotate}
            icon={icon}
            preserveIconColour={preserveIconColour}
            ligatureStyleOverride={ligatureStyleOverride}
          />
        : ''
      }
      {
        multiIcons.length !== 0 ?
        (
          multiIcons.map((iconName, i) => (
            <IconWithTextItalic
              key={iconName + i.toString()}
              /* eslint-disable */
              /*
                Let rotate override per-icon rotation.
                If multiIconsRotate is given and it has the same number of entries as the number of icons given, then apply
                rotation to each icon in order.
              */
              rotate={rotate ? rotate : multiIconsRotate[i] || 0}
              /* eslint-enable */
              icon={iconName}
              preserveIconColour={preserveIconColour}
              ligatureStyleOverride={ligatureStyleOverride}
            />
          ))
        ) : ''
      }
      {
        textOrientation === 'after' ?
          <React.Fragment>
            {
            roundedButton ?
              <IconWithTextSpanMultiLine
                inverseColour={inverseColour}
                roundedButton={roundedButton}
                text={text}
                textAlign={textAlign}
              />
            :
              <React.Fragment>
                {roundedButton ? '' : '\u00A0'}<IconWithTextSpan inverseColour={inverseColour} roundedButton={roundedButton} squareButton={squareButton} text={text} textAlign={textAlign} />
              </React.Fragment>
          }
          </React.Fragment> : ''
      }
    </button>
  </div>
);

IconWithText.propTypes = {
  onClick: PropTypes.func,
  textOrientation: PropTypes.oneOf([
    'before',
    'after',
  ]),
  textAlign: PropTypes.string,
  text: PropTypes.string,
  icon: PropTypes.string,
  multiIcons: PropTypes.arrayOf(icons),
  rotate: iconRotation,
  multiIconsRotate: PropTypes.arrayOf(iconRotation),
  inverseColour: PropTypes.bool,
  preserveIconColour: PropTypes.bool,
  ligatureStyleOverride: ligatureStyleOverrides,
  roundedButton: PropTypes.bool,
  squareButton: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  isFullWidth: PropTypes.bool,
};

IconWithText.defaultProps = {
  icon: '',
  multiIcons: [],
  multiIconsRotate: [],
  inverseColour: false,
  preserveIconColour: false,
  ligatureStyleOverride: {},
  roundedButton: false,
  isFullWidth: false,
  textAlign: '',
};

export default IconWithText;



// WEBPACK FOOTER //
// ./src/components/IconWithText/IconWithText.jsx